/**
 * User Preference Profiling System
 * Analyzes user's saved products to build preference profiles for personalized recommendations
 */

import { analyzeProductCategories, categorizeProduct } from './productCategorizer.js';

/**
 * Builds a comprehensive user preference profile based on their wishlists and products
 * @param {Array} userWishlists - All wishlists belonging to the user
 * @param {Array} likedWishlists - Wishlists the user has liked
 * @param {Array} followingUsers - Users the user follows
 * @returns {Object} - Complete user preference profile
 */
export function buildUserProfile(userWishlists = [], likedWishlists = [], followingUsers = []) {
  // Collect all products from user's wishlists
  const allUserProducts = [];
  userWishlists.forEach(wishlist => {
    if (wishlist.products && Array.isArray(wishlist.products)) {
      wishlist.products.forEach(product => {
        allUserProducts.push({
          ...product,
          source: 'owned',
          wishlistId: wishlist.id,
          wishlistName: wishlist.name,
          addedAt: product.addedAt || wishlist.createdAt
        });
      });
    }
  });

  // Collect products from liked wishlists
  const likedProducts = [];
  likedWishlists.forEach(wishlist => {
    if (wishlist.products && Array.isArray(wishlist.products)) {
      wishlist.products.forEach(product => {
        likedProducts.push({
          ...product,
          source: 'liked',
          wishlistId: wishlist.id,
          wishlistName: wishlist.name,
          wishlistOwner: wishlist.userId
        });
      });
    }
  });

  // Analyze category preferences
  const ownedProductAnalysis = analyzeProductCategories(allUserProducts);
  const likedProductAnalysis = analyzeProductCategories(likedProducts);

  // Build weighted preference scores
  const preferences = buildPreferenceScores(ownedProductAnalysis, likedProductAnalysis);

  // Analyze price preferences
  const pricePreferences = analyzePricePreferences(allUserProducts, likedProducts);

  // Analyze brand preferences
  const brandPreferences = analyzeBrandPreferences(allUserProducts, likedProducts);

  // Analyze temporal patterns
  const temporalPatterns = analyzeTemporalPatterns(allUserProducts);

  // Social preferences
  const socialPreferences = analyzeSocialPreferences(followingUsers, likedWishlists);

  return {
    userId: userWishlists[0]?.userId || null,
    lastUpdated: new Date().toISOString(),
    
    // Product preferences
    categoryPreferences: preferences,
    pricePreferences,
    brandPreferences,
    
    // Behavioral patterns
    temporalPatterns,
    socialPreferences,
    
    // Statistics
    stats: {
      totalOwnedProducts: allUserProducts.length,
      totalLikedProducts: likedProducts.length,
      totalWishlists: userWishlists.length,
      totalLikedWishlists: likedWishlists.length,
      followingCount: followingUsers.length,
      profileStrength: calculateProfileStrength(allUserProducts, likedProducts, followingUsers)
    },

    // Raw data for debugging
    debug: {
      ownedProductAnalysis,
      likedProductAnalysis
    }
  };
}

/**
 * Builds weighted preference scores combining owned and liked products
 * @param {Object} ownedAnalysis - Analysis of user's owned products
 * @param {Object} likedAnalysis - Analysis of user's liked products
 * @returns {Object} - Weighted category preferences
 */
function buildPreferenceScores(ownedAnalysis, likedAnalysis) {
  const preferences = {};
  const OWNED_WEIGHT = 1.0;  // Full weight for owned products
  const LIKED_WEIGHT = 0.7;  // Slightly less weight for liked products

  // Process owned products (higher weight)
  Object.entries(ownedAnalysis.distribution || {}).forEach(([category, data]) => {
    if (!preferences[category]) {
      preferences[category] = {
        score: 0,
        count: 0,
        sources: { owned: 0, liked: 0 }
      };
    }
    preferences[category].score += data.percentage * OWNED_WEIGHT;
    preferences[category].count += data.count;
    preferences[category].sources.owned = data.count;
  });

  // Process liked products (lower weight)
  Object.entries(likedAnalysis.distribution || {}).forEach(([category, data]) => {
    if (!preferences[category]) {
      preferences[category] = {
        score: 0,
        count: 0,
        sources: { owned: 0, liked: 0 }
      };
    }
    preferences[category].score += data.percentage * LIKED_WEIGHT;
    preferences[category].sources.liked = data.count;
  });

  // Normalize scores
  const totalScore = Object.values(preferences).reduce((sum, pref) => sum + pref.score, 0);
  if (totalScore > 0) {
    Object.keys(preferences).forEach(category => {
      preferences[category].normalizedScore = preferences[category].score / totalScore;
    });
  }

  return preferences;
}

/**
 * Analyzes price preferences from user's products
 * @param {Array} ownedProducts - User's owned products
 * @param {Array} likedProducts - User's liked products
 * @returns {Object} - Price preference analysis
 */
function analyzePricePreferences(ownedProducts, likedProducts) {
  const allProducts = [...ownedProducts, ...likedProducts];
  const prices = [];

  // Extract prices from product URLs and titles
  allProducts.forEach(product => {
    const price = extractPriceFromProduct(product);
    if (price && price > 0) {
      prices.push(price);
    }
  });

  if (prices.length === 0) {
    return { range: 'unknown', average: 0, distribution: {} };
  }

  prices.sort((a, b) => a - b);
  const average = prices.reduce((sum, price) => sum + price, 0) / prices.length;
  const median = prices[Math.floor(prices.length / 2)];
  const min = prices[0];
  const max = prices[prices.length - 1];

  // Categorize price ranges
  const ranges = {
    budget: prices.filter(p => p < 50).length,
    mid: prices.filter(p => p >= 50 && p < 200).length,
    premium: prices.filter(p => p >= 200 && p < 500).length,
    luxury: prices.filter(p => p >= 500).length
  };

  const preferredRange = Object.entries(ranges).reduce((a, b) => 
    ranges[a[0]] > ranges[b[0]] ? a : b
  )[0];

  return {
    range: preferredRange,
    average,
    median,
    min,
    max,
    distribution: ranges,
    sampleSize: prices.length
  };
}

/**
 * Extracts price from product title or URL (basic implementation)
 * @param {Object} product - Product object
 * @returns {number|null} - Extracted price or null
 */
function extractPriceFromProduct(product) {
  const text = `${product.title || ''} ${product.url || ''}`.toLowerCase();
  
  // Common price patterns
  const patterns = [
    /[\$€£¥](\d+(?:\.\d{2})?)/,  // $99.99, €149.50, etc.
    /(\d+(?:\.\d{2})?)[\$€£¥]/,  // 99.99$, 149.50€, etc.
    /price[:\s]*[\$€£¥]?(\d+(?:\.\d{2})?)/,  // price: $99.99
    /(\d+)[\s]*(?:euro|dollar|usd|eur|gbp)/,  // 99 euro, 149 dollar
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match) {
      const price = parseFloat(match[1]);
      if (!isNaN(price) && price > 0) {
        return price;
      }
    }
  }

  return null;
}

/**
 * Analyzes brand preferences from product URLs and titles
 * @param {Array} ownedProducts - User's owned products
 * @param {Array} likedProducts - User's liked products
 * @returns {Object} - Brand preference analysis
 */
function analyzeBrandPreferences(ownedProducts, likedProducts) {
  const brandCounts = {};
  const allProducts = [...ownedProducts, ...likedProducts];

  allProducts.forEach(product => {
    const brands = extractBrandsFromProduct(product);
    brands.forEach(brand => {
      if (!brandCounts[brand]) {
        brandCounts[brand] = 0;
      }
      brandCounts[brand]++;
    });
  });

  // Sort brands by frequency
  const sortedBrands = Object.entries(brandCounts)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 10) // Top 10 brands
    .map(([brand, count]) => ({
      brand,
      count,
      percentage: count / allProducts.length
    }));

  return {
    topBrands: sortedBrands,
    totalBrands: Object.keys(brandCounts).length,
    brandLoyalty: calculateBrandLoyalty(sortedBrands)
  };
}

/**
 * Extracts brand names from product URL and title
 * @param {Object} product - Product object
 * @returns {Array} - Array of detected brand names
 */
function extractBrandsFromProduct(product) {
  const brands = [];
  const url = (product.url || '').toLowerCase();
  const title = (product.title || '').toLowerCase();

  // Common brand domains
  const brandDomains = {
    'nike.com': 'Nike',
    'adidas.com': 'Adidas',
    'apple.com': 'Apple',
    'samsung.com': 'Samsung',
    'zara.com': 'Zara',
    'hm.com': 'H&M',
    'uniqlo.com': 'Uniqlo',
    'amazon.com': 'Amazon',
    'target.com': 'Target',
    'walmart.com': 'Walmart',
    'ikea.com': 'IKEA',
    'gucci.com': 'Gucci',
    'prada.com': 'Prada',
    'louisvuitton.com': 'Louis Vuitton'
  };

  // Check URL domain
  Object.entries(brandDomains).forEach(([domain, brand]) => {
    if (url.includes(domain)) {
      brands.push(brand);
    }
  });

  // Check title for brand names
  const brandKeywords = Object.values(brandDomains);
  brandKeywords.forEach(brand => {
    if (title.includes(brand.toLowerCase())) {
      brands.push(brand);
    }
  });

  return [...new Set(brands)]; // Remove duplicates
}

/**
 * Calculates brand loyalty score
 * @param {Array} sortedBrands - Sorted brand preferences
 * @returns {number} - Brand loyalty score (0-1)
 */
function calculateBrandLoyalty(sortedBrands) {
  if (sortedBrands.length === 0) return 0;
  
  const topBrandPercentage = sortedBrands[0]?.percentage || 0;
  const brandConcentration = sortedBrands.slice(0, 3).reduce((sum, brand) => sum + brand.percentage, 0);
  
  return Math.min(topBrandPercentage * 2 + brandConcentration * 0.5, 1);
}

/**
 * Analyzes temporal patterns in user behavior
 * @param {Array} ownedProducts - User's owned products
 * @returns {Object} - Temporal pattern analysis
 */
function analyzeTemporalPatterns(ownedProducts) {
  const patterns = {
    hourly: new Array(24).fill(0),
    daily: new Array(7).fill(0),
    monthly: new Array(12).fill(0),
    activity: {
      morning: 0,    // 6-12
      afternoon: 0,  // 12-18
      evening: 0,    // 18-24
      night: 0       // 0-6
    }
  };

  ownedProducts.forEach(product => {
    const date = new Date(product.addedAt || Date.now());
    const hour = date.getHours();
    const day = date.getDay();
    const month = date.getMonth();

    patterns.hourly[hour]++;
    patterns.daily[day]++;
    patterns.monthly[month]++;

    // Activity periods
    if (hour >= 6 && hour < 12) patterns.activity.morning++;
    else if (hour >= 12 && hour < 18) patterns.activity.afternoon++;
    else if (hour >= 18 && hour < 24) patterns.activity.evening++;
    else patterns.activity.night++;
  });

  return patterns;
}

/**
 * Analyzes social preferences and connections
 * @param {Array} followingUsers - Users the user follows
 * @param {Array} likedWishlists - Wishlists the user has liked
 * @returns {Object} - Social preference analysis
 */
function analyzeSocialPreferences(followingUsers, likedWishlists) {
  const influencerTypes = {};
  const socialActivity = {
    followsInfluencers: followingUsers.length > 10,
    likesFrequently: likedWishlists.length > 5,
    socialScore: Math.min((followingUsers.length * 0.1 + likedWishlists.length * 0.2), 10)
  };

  // Analyze types of users they follow (could be enhanced with more data)
  likedWishlists.forEach(wishlist => {
    const ownerType = categorizeUser(wishlist);
    if (!influencerTypes[ownerType]) {
      influencerTypes[ownerType] = 0;
    }
    influencerTypes[ownerType]++;
  });

  return {
    socialActivity,
    influencerTypes,
    networkSize: followingUsers.length,
    engagementLevel: calculateEngagementLevel(followingUsers.length, likedWishlists.length)
  };
}

/**
 * Categorizes a user based on their wishlist patterns (basic implementation)
 * @param {Object} wishlist - Wishlist object
 * @returns {string} - User category
 */
function categorizeUser(wishlist) {
  const productCount = (wishlist.products || []).length;
  const likesCount = wishlist.likesCount || 0;

  if (likesCount > 50) return 'influencer';
  if (productCount > 20) return 'curator';
  if (likesCount > 10) return 'popular';
  return 'regular';
}

/**
 * Calculates user engagement level
 * @param {number} followingCount - Number of users followed
 * @param {number} likedCount - Number of wishlists liked
 * @returns {string} - Engagement level
 */
function calculateEngagementLevel(followingCount, likedCount) {
  const totalActivity = followingCount + likedCount * 2;
  
  if (totalActivity > 50) return 'high';
  if (totalActivity > 20) return 'medium';
  if (totalActivity > 5) return 'low';
  return 'minimal';
}

/**
 * Calculates overall profile strength
 * @param {Array} ownedProducts - User's owned products
 * @param {Array} likedProducts - User's liked products
 * @param {Array} followingUsers - Users followed
 * @returns {number} - Profile strength score (0-1)
 */
function calculateProfileStrength(ownedProducts, likedProducts, followingUsers) {
  let score = 0;
  
  // Product data strength
  if (ownedProducts.length > 0) score += 0.4;
  if (ownedProducts.length > 10) score += 0.2;
  
  // Social data strength
  if (likedProducts.length > 0) score += 0.2;
  if (followingUsers.length > 0) score += 0.1;
  
  // Engagement strength
  if (likedProducts.length > 5) score += 0.1;
  
  return Math.min(score, 1);
}

/**
 * Compares two user profiles for similarity
 * @param {Object} profile1 - First user profile
 * @param {Object} profile2 - Second user profile
 * @returns {number} - Similarity score (0-1)
 */
export function calculateProfileSimilarity(profile1, profile2) {
  if (!profile1 || !profile2) return 0;

  let similarity = 0;
  let factors = 0;

  // Category preferences similarity
  const categories1 = Object.keys(profile1.categoryPreferences || {});
  const categories2 = Object.keys(profile2.categoryPreferences || {});
  const commonCategories = categories1.filter(cat => categories2.includes(cat));
  
  if (commonCategories.length > 0) {
    let categoryOverlap = 0;
    commonCategories.forEach(category => {
      const score1 = profile1.categoryPreferences[category].normalizedScore || 0;
      const score2 = profile2.categoryPreferences[category].normalizedScore || 0;
      categoryOverlap += 1 - Math.abs(score1 - score2);
    });
    similarity += (categoryOverlap / commonCategories.length) * 0.5;
    factors += 0.5;
  }

  // Price range similarity
  const price1 = profile1.pricePreferences?.range;
  const price2 = profile2.pricePreferences?.range;
  if (price1 && price2) {
    similarity += (price1 === price2 ? 1 : 0) * 0.2;
    factors += 0.2;
  }

  // Brand overlap
  const brands1 = profile1.brandPreferences?.topBrands?.map(b => b.brand) || [];
  const brands2 = profile2.brandPreferences?.topBrands?.map(b => b.brand) || [];
  const commonBrands = brands1.filter(brand => brands2.includes(brand));
  if (brands1.length > 0 && brands2.length > 0) {
    const brandSimilarity = commonBrands.length / Math.max(brands1.length, brands2.length);
    similarity += brandSimilarity * 0.3;
    factors += 0.3;
  }

  return factors > 0 ? similarity / factors : 0;
}

/**
 * Gets user recommendations based on profile
 * @param {Object} userProfile - User's preference profile
 * @param {Array} availableWishlists - Available wishlists to recommend from
 * @param {Array} userProfiles - Other user profiles for similarity matching
 * @returns {Array} - Recommended wishlists with scores
 */
export function getPersonalizedRecommendations(userProfile, availableWishlists, userProfiles = []) {
  if (!userProfile || !availableWishlists) return [];

  const recommendations = availableWishlists.map(wishlist => {
    let score = 0;
    let factors = [];

    // Category matching
    const wishlistCategories = analyzeProductCategories(wishlist.products || []);
    Object.entries(userProfile.categoryPreferences || {}).forEach(([category, preference]) => {
      const wishlistCategoryData = wishlistCategories.distribution[category];
      if (wishlistCategoryData) {
        const categoryScore = preference.normalizedScore * wishlistCategoryData.percentage * 10;
        score += categoryScore;
        factors.push({ type: 'category', category, score: categoryScore });
      }
    });

    // Social signals (collaborative filtering)
    const ownerProfile = userProfiles.find(p => p.userId === wishlist.userId);
    if (ownerProfile) {
      const similarity = calculateProfileSimilarity(userProfile, ownerProfile);
      score += similarity * 5;
      factors.push({ type: 'similarity', score: similarity * 5 });
    }

    // Popularity boost
    const popularityScore = Math.log(1 + (wishlist.likesCount || 0)) * 0.5;
    score += popularityScore;
    factors.push({ type: 'popularity', score: popularityScore });

    // Recency boost
    const daysOld = (Date.now() - new Date(wishlist.createdAt).getTime()) / (1000 * 60 * 60 * 24);
    const recencyScore = Math.max(0, 5 - daysOld * 0.1);
    score += recencyScore;
    factors.push({ type: 'recency', score: recencyScore });

    return {
      ...wishlist,
      recommendationScore: score,
      factors
    };
  });

  // Sort by score and return top recommendations
  return recommendations
    .sort((a, b) => b.recommendationScore - a.recommendationScore)
    .slice(0, 20); // Top 20 recommendations
}

export default {
  buildUserProfile,
  calculateProfileSimilarity,
  getPersonalizedRecommendations
};